import { useEffect, useRef, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import BadgeIcon from "@mui/icons-material/BadgeOutlined"
import BlockIcon from "@mui/icons-material/BlockOutlined"
import DeleteIcon from "@mui/icons-material/DeleteOutline"
import EditIcon from "@mui/icons-material/EditOutlined"
import ExpandIcon from "@mui/icons-material/ExpandMore"
import KeyIcon from "@mui/icons-material/KeyOutlined"
import LogoutIcon from "@mui/icons-material/LogoutOutlined"
import MailIcon from "@mui/icons-material/ForwardToInboxOutlined"
import RestoreIcon from "@mui/icons-material/SettingsBackupRestoreOutlined"

import { authApi, generalApi, userApi } from "../../api"
import { usePermissions } from "../../permissions"
import { useAuth } from "../../context/AuthContext"
import PasswordStrength from "../../components/PasswordStrength"
import ChangeRoleDialog from "./ChangeRoleDialog"

/**
 * Everything that can be done to an account, from the person's own page.
 *
 * ── One menu, each item its own permission ──────────────────────────────────
 *
 * The Users module's account actions are separate grants — editing someone's
 * details, changing their role, emailing them a link, typing a password for
 * them, signing them out, turning them off — and the menu offers exactly the
 * ones the viewer holds. An item the server would refuse is not shown, because
 * a button that ends in "permission denied" reads as the app being broken.
 *
 * ── The rules that are not permissions ──────────────────────────────────────
 *
 * Some things are refused whoever asks: nobody deactivates themselves, the
 * system account has no mailbox and does not sign in, a deactivated account is
 * sent nothing until it is turned back on. Those are left out of the menu too,
 * for the same reason.
 *
 * @param {object} props
 * @param {object} props.person The account, as GET /users/:id returns it.
 * @param {(person: object|null, message?: string) => void} props.onChanged
 *   Called with the updated account (or null when it no longer exists), and a
 *   sentence saying what happened.
 */
export default function PersonActions({ person, onChanged, onError }) {
  const { can } = usePermissions()
  const { user: viewer } = useAuth()
  const anchor = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState(null) // "edit" | "role" | "password" | { confirm }
  const [roles, setRoles] = useState([])

  const isSelf = Number(viewer?.id) === Number(person.id)
  const isSystem = Number(person.id) === 1
  const status = person.status

  const items = [
    can("users", "update") || can("users", "change_username")
      ? { key: "edit", label: "Edit details", icon: EditIcon, run: () => setDialog("edit") }
      : null,
    can("users", "assign_role") && !isSystem
      ? { key: "role", label: "Change role", icon: BadgeIcon, run: () => setDialog("role") }
      : null,
    "divider",
    can("users", "reset_password") && !isSystem && status !== "deactivated"
      ? {
          key: "resend",
          label: status === "invited" ? "Resend invite" : "Email a password reset link",
          icon: MailIcon,
          run: () => resend(),
        }
      : null,
    can("users", "set_password") && !isSystem && status !== "deactivated"
      ? { key: "password", label: "Set a password…", icon: KeyIcon, run: () => setDialog("password") }
      : null,
    can("users", "manage_sessions") && !isSystem && !isSelf
      ? {
          key: "signout",
          label: "Sign out everywhere",
          icon: LogoutIcon,
          run: () =>
            setDialog({
              confirm: {
                title: `Sign ${person.name || person.username} out everywhere?`,
                body: "Every device they are signed in on is signed out on its next request. Their account is untouched — they sign in again like anyone else.",
                action: "Sign out everywhere",
                run: async () => {
                  const result = await userApi.endSessions(person.id)
                  onChanged(person, `Signed out of ${result.sessions_revoked} session${result.sessions_revoked === 1 ? "" : "s"}.`)
                },
              },
            }),
        }
      : null,
    "divider",
    can("users", "deactivate") && !isSystem && !isSelf && person.active
      ? {
          key: "deactivate",
          label: "Deactivate account",
          icon: BlockIcon,
          danger: true,
          run: () =>
            setDialog({
              confirm: {
                title: `Deactivate ${person.name || person.username}?`,
                body: "They are signed out of every device and cannot sign in until the account is turned back on. Nothing they did is removed — their tasks, comments and hours keep their name.",
                action: "Deactivate",
                danger: true,
                run: async () => {
                  await userApi.deactivate(person.id)
                  const fresh = await userApi.get(person.id)
                  onChanged(fresh.user, `${person.name || person.username} was deactivated.`)
                },
              },
            }),
        }
      : null,
    can("users", "deactivate") && !isSystem && !person.active
      ? {
          key: "reactivate",
          label: "Reactivate account",
          icon: RestoreIcon,
          run: async () => {
            try {
              const result = await userApi.activate(person.id)
              onChanged(result.user, result.message)
            } catch (err) {
              onError(err.userMessage || "That account could not be reactivated.")
            }
          },
        }
      : null,
    can("users", "delete") && !isSystem && !isSelf && status === "invited" && !person.last_access_on
      ? {
          key: "withdraw",
          label: "Withdraw invite",
          icon: DeleteIcon,
          danger: true,
          run: () =>
            setDialog({
              confirm: {
                title: `Withdraw the invite for ${person.name || person.username}?`,
                body: "The account is deleted outright — it has never been used. Only an invite nobody accepted can be removed this way; anything else is deactivated instead.",
                action: "Withdraw invite",
                danger: true,
                run: async () => {
                  const result = await userApi.withdrawInvite(person.id)
                  onChanged(null, result.message)
                },
              },
            }),
        }
      : null,
  ]

  // Dividers only between groups that both have something in them.
  const visible = items.filter(Boolean).filter((item, index, list) => {
    if (item !== "divider") return true
    const before = list.slice(0, index).some((other) => other !== "divider")
    const after = list.slice(index + 1).some((other) => other !== "divider")
    const previous = list[index - 1]
    return before && after && previous !== "divider"
  })

  useEffect(() => {
    if (dialog !== "role" || roles.length) return
    authApi
      .roles()
      .then((data) => setRoles(data.roles || []))
      .catch(() => setRoles([]))
  }, [dialog, roles.length])

  const resend = async () => {
    try {
      const result = await userApi.resend(person.id)
      if (result.email_sent) onChanged(person, result.message)
      else if (!result.email_configured) onError(`Nothing was sent to ${result.email} — email is not configured on this deployment.`)
      else onError(result.message)
    } catch (err) {
      onError(err.userMessage || "That email could not be sent.")
    }
  }

  if (visible.length === 0) return null

  return (
    <>
      <Button
        ref={anchor}
        variant="contained"
        endIcon={<ExpandIcon />}
        onClick={() => setMenuOpen(true)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        Manage
      </Button>
      <Menu
        anchorEl={anchor.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 250 } } }}
      >
        {visible.map((item, index) =>
          item === "divider" ? (
            <Divider key={`divider-${index}`} />
          ) : (
            <MenuItem
              key={item.key}
              onClick={() => {
                setMenuOpen(false)
                item.run()
              }}
              sx={item.danger ? { color: "error.main" } : undefined}
            >
              <ListItemIcon sx={item.danger ? { color: "error.main" } : undefined}>
                <item.icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ),
        )}
      </Menu>

      <EditPersonDialog
        open={dialog === "edit"}
        person={person}
        onClose={() => setDialog(null)}
        onSaved={(updated) => {
          setDialog(null)
          onChanged(updated, "Details saved.")
        }}
      />

      <ChangeRoleDialog
        open={dialog === "role"}
        user={person}
        roles={roles}
        onClose={() => setDialog(null)}
        onSave={async (userId, roleId) => {
          try {
            const result = await userApi.assignRole(userId, roleId === "" ? null : Number(roleId))
            setDialog(null)
            onChanged(
              result.user,
              result.sessions_revoked > 0 ? "Role changed. They were signed out so it takes effect at once." : "Role changed.",
            )
          } catch (err) {
            onError(err.userMessage || "That role could not be set.")
          }
        }}
      />

      <SetPasswordDialog
        open={dialog === "password"}
        person={person}
        onClose={() => setDialog(null)}
        onSendLink={
          can("users", "reset_password")
            ? () => {
                setDialog(null)
                resend()
              }
            : null
        }
        onSaved={() => {
          setDialog(null)
          onChanged(person, "Password set. They were signed out of every device and sign in with the new one.")
        }}
      />

      <ConfirmDialog
        confirm={dialog?.confirm || null}
        onClose={() => setDialog(null)}
        onError={onError}
      />
    </>
  )
}

/**
 * Name, address, title, phone, department — and the sign-in name, for those
 * allowed to change it.
 *
 * The sign-in name is checked as it is typed, the way the add-user form does,
 * so a clash shows before Save rather than as a refusal after it.
 */
function EditPersonDialog({ open, person, onClose, onSaved }) {
  const { can } = usePermissions()
  const [form, setForm] = useState(null)
  const [departments, setDepartments] = useState([])
  const [usernameState, setUsernameState] = useState(null) // null | "checking" | { available, message }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const mayEditDetails = can("users", "update")
  const mayRename = can("users", "change_username") && Number(person.id) !== 1

  useEffect(() => {
    if (!open) return
    setError(null)
    setUsernameState(null)
    setForm({
      name: person.name || "",
      email: person.email || "",
      title: person.title || "",
      phone: person.phone || "",
      department_id: person.department_id ? String(person.department_id) : "",
      username: person.username || "",
    })
    generalApi
      .departments()
      .then((data) => setDepartments(data.departments || []))
      .catch(() => setDepartments([]))
  }, [open, person])

  // The sign-in name, checked while typed — debounced, and only when changed.
  useEffect(() => {
    if (!open || !form || !mayRename) return
    const next = form.username.trim().toLowerCase()
    if (!next || next === person.username) {
      setUsernameState(null)
      return
    }
    setUsernameState("checking")
    const timer = setTimeout(() => {
      userApi
        .checkUsername(next)
        .then((result) => setUsernameState({ available: Boolean(result.available), message: result.message || result.reason }))
        .catch(() => setUsernameState(null))
    }, 350)
    return () => clearTimeout(timer)
  }, [open, form, mayRename, person.username])

  if (!form) return null
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }))
  const usernameBlocked = usernameState === "checking" || (usernameState && !usernameState.available)

  const save = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const payload = {}
      if (mayEditDetails) {
        Object.assign(payload, {
          name: form.name,
          email: form.email,
          title: form.title,
          phone: form.phone,
          department_id: form.department_id === "" ? null : Number(form.department_id),
        })
      }
      if (mayRename && form.username.trim().toLowerCase() !== person.username) payload.username = form.username
      const result = await userApi.update(person.id, payload)
      onSaved(result.user)
    } catch (err) {
      setError(err.userMessage || "Those changes could not be saved.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={save}>
        <DialogTitle>Edit {person.name || person.username}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Full name" value={form.name} onChange={set("name")} fullWidth disabled={!mayEditDetails} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Signs in as"
                value={form.username}
                onChange={set("username")}
                fullWidth
                disabled={!mayRename}
                error={Boolean(usernameState && usernameState !== "checking" && !usernameState.available)}
                helperText={
                  !mayRename
                    ? "Changing it needs the Change Username permission"
                    : usernameState === "checking"
                      ? "Checking…"
                      : usernameState
                        ? usernameState.available
                          ? "Free — they will sign in with this from now on"
                          : usernameState.message || "Already taken"
                        : "What they type to sign in"
                }
                slotProps={{ input: { sx: { fontFamily: "monospace" } } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField label="Email" type="email" value={form.email} onChange={set("email")} fullWidth disabled={!mayEditDetails} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Job title" value={form.title} onChange={set("title")} fullWidth disabled={!mayEditDetails} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone" value={form.phone} onChange={set("phone")} fullWidth disabled={!mayEditDetails} />
            </Grid>
            <Grid size={12}>
              <TextField
                select
                label="Department"
                value={form.department_id}
                onChange={set("department_id")}
                fullWidth
                disabled={!mayEditDetails}
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={String(department.id)}>
                    {department.label || department.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || usernameBlocked}>
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/**
 * Typing a password for somebody — break-glass.
 *
 * A form, because the person granted Set Password was granted it on purpose.
 * It still says, before anything else, that the link is the better way: a
 * password typed here has to reach them somehow, and that channel is the
 * thing the emailed link exists to avoid.
 */
function SetPasswordDialog({ open, person, onClose, onSaved, onSendLink }) {
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      setPassword("")
      setConfirm("")
      setError(null)
    }
  }, [open])

  const tooShort = password.length > 0 && password.length < 10
  const mismatch = confirm.length > 0 && confirm !== password
  const ready = password.length >= 10 && confirm === password

  const save = async (event) => {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await userApi.resetPassword(person.id, password)
      onSaved()
    } catch (err) {
      setError(err.userMessage || "That password could not be set.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={save}>
        <DialogTitle>Set a password for {person.name || person.username}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Prefer the emailed link: only they ever see it. A password typed here has to reach them some other way.
            {onSendLink ? (
              <Button size="small" color="inherit" onClick={onSendLink} sx={{ display: "block", mt: 0.5, px: 0, textDecoration: "underline" }}>
                Email them a link instead
              </Button>
            ) : null}
          </Alert>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <TextField
            label="New password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
            autoFocus
            autoComplete="new-password"
            error={tooShort}
            helperText={tooShort ? "At least 10 characters" : " "}
            sx={{ mb: 1 }}
          />
          <PasswordStrength value={password} sx={{ mb: 2 }} />
          <TextField
            label="Type it again"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            fullWidth
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? "The two do not match" : " "}
          />
          <Typography variant="caption" color="text.secondary">
            They are signed out of every device when it is saved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !ready}>
            {busy ? "Setting…" : "Set password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** "Are you sure", with what will actually happen said in a sentence. */
function ConfirmDialog({ confirm, onClose, onError }) {
  const [busy, setBusy] = useState(false)
  return (
    <Dialog open={Boolean(confirm)} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{confirm?.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {confirm?.body}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          color={confirm?.danger ? "error" : "primary"}
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            try {
              await confirm.run()
              onClose()
            } catch (err) {
              onError(err.userMessage || "That did not go through.")
              onClose()
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? "Working…" : confirm?.action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
